import {
  Avatar,
  Box,
  Chip,
  Dialog,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import { toast } from '../toast';
import { downloadEmailAttachment } from '../../services/api';
import { formatBytes, getFileIcon } from './emailAttachmentUtils';

const STATUS_COLORS = { Success: 'success', Failure: 'error' };

function senderInitials(emails) {
  if (!emails) return '?';
  const first = emails.split(',')[0].trim();
  return first[0]?.toUpperCase() ?? '?';
}

export default function EmailViewDialog({ log, onClose }) {
  if (!log) return null;

  const handleDownload = async (logId, attachmentId, fileName) => {
    try {
      const res = await downloadEmailAttachment(logId, attachmentId);
      window.open(res.data.download_url, '_blank');
    } catch (err) {
      console.error(err);
      toast.error(`Failed to download '${fileName}'`);
    }
  };

  const recipients = log.recipient_emails || '—';
  const subject = log.subject || '(no subject)';
  const sentAt = log.created_at
    ? dayjs(log.created_at).format('MMM D, YYYY [at] h:mm A')
    : '—';

  const hasFooter = log.error_message || (log.attachments && log.attachments.length > 0);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Typography variant="h6" fontWeight={600} sx={{ wordBreak: 'break-word', lineHeight: 1.35 }}>
            {subject}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0, mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1.5} mt={1.5}>
          <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 14, flexShrink: 0 }}>
            {senderInitials(recipients)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Typography variant="body2" fontWeight={700}>BillerBay Transmit</Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                <Chip
                  label={log.status}
                  size="small"
                  color={STATUS_COLORS[log.status] ?? 'default'}
                  variant="outlined"
                  sx={{ fontSize: 11, height: 20 }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>{sentAt}</Typography>
              </Stack>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block">to {recipients}</Typography>
            {log.cc_emails && (
              <Typography variant="caption" color="text.secondary" display="block">cc {log.cc_emails}</Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Email preview — fills all remaining space */}
      <Box sx={{ flex: 1, overflow: 'hidden', bgcolor: '#f4f8fc' }}>
        {(log.html || log.body) ? (
          <iframe
            srcDoc={log.html || log.body}
            title="Email preview"
            sandbox="allow-same-origin"
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography color="text.secondary" variant="body2">(no content)</Typography>
          </Box>
        )}
      </Box>

      {/* Footer: error + attachments (only rendered when present) */}
      {hasFooter && (
        <Box sx={{ px: 3, py: 2, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider', overflowY: 'auto', maxHeight: 180 }}>
          {log.error_message && (
            <Box sx={{ mb: log.attachments?.length ? 1.5 : 0, p: 1.5, bgcolor: 'error.lighter', borderRadius: 1 }}>
              <Typography variant="caption" color="error.dark">
                Error: {log.error_message}
              </Typography>
            </Box>
          )}

          {log.attachments && log.attachments.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Attachments ({log.attachments.length})
              </Typography>
              <Stack spacing={0.5} mt={1}>
                {log.attachments.map((att) => (
                  <Stack
                    key={att.id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1.5, py: 0.75,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.neutral',
                    }}
                  >
                    {getFileIcon(att.file_name)}
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                      <Typography variant="body2" noWrap title={att.file_name}>{att.file_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatBytes(att.file_size)}</Typography>
                    </Box>
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => handleDownload(log.id, att.id, att.file_name)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            </>
          )}
        </Box>
      )}
    </Dialog>
  );
}
